import { Directive, Input, ViewChild } from '@angular/core';
import { iif, Observable, of } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import { isNullOrUndefined } from '@app/util/util';

import { BusResultsFacade } from '@buses/bus-results/bus-results.facade';
import { FlightResultsFacade } from '@flight-results/flight-results.facade';
import * as filterMappers from '@flight-results/mappers/filters.mapper';
import { FilterApplier, FilterTypes } from '@shared/flight-itinerary/entities/filters';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { AMPLITUDE_EVENTS } from '@app/modules/core/constants/amplitude.constants';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { FILTER_TITLES } from '@flight-results/constants/filters.constants';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { environment as ENV } from '@environment';
import { 
  MobileFilterMagicContComponent 
} from '@shared/magic-filter-bar/mobile-filter-magic-cont/mobile-filter-magic-cont.component';

@Directive()
export abstract class FlightsBaseFiltersAbstract {

  @Input() isBySector: boolean;
  @Input() isChanges: boolean;

  @ViewChild(MobileFilterMagicContComponent) mobileFilterMagicContComponent : MobileFilterMagicContComponent;

  public isBuses: boolean;
  public isFilterPrice: boolean;
  public isMobile = false;
  public currencyCode = ENV.countryInfo.currency_code;

  constructor(
    protected fligthfacade: FlightResultsFacade,
    protected busFacade: BusResultsFacade,
    protected amplitude: PropertiesAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected domManipulateService: DomManipulateService
  ) { }

  get orderCriteria$(): Observable<SorterTypes> {
    return this.fligthfacade.orderCriteria$;
  }

  get orderDesc$(): Observable<boolean> {
    return this.fligthfacade.orderDesc$;
  }

  public getItemLabelProps(type: string): { [key: string]: string } {
    if (type === FilterTypes.PriceRange) {
      return { currency: this.currencyCode };
    } else {
      return {};
    }
  }

  public getItemLabel(label: string): string {
    switch (label) {
      case FilterTypes.Airlines:
        return this.isBuses ? 'FLIGHT_RESULTS_MODULE.FILTER_SECTION.COMPANY' : 'FLIGHT_RESULTS_MODULE.FILTER_SECTION.AEROLINE';
      case FilterTypes.BusClass:
        return 'BUS_RESULTS_MODULE.FILTER_SECTION.BUS_CLASS';
      case FilterTypes.SectorDurations:
        return this.isBuses ?
          'BUS_RESULTS_MODULE.FILTER_SECTION.TRAVEL_DURATION' :
          'FLIGHT_RESULTS_MODULE.FILTER_SECTION.FLIGHT_DURATION';
      case FilterTypes.DepartureAirports:
        return 'FLIGHT_RESULTS_MODULE.FILTER_SECTION.FLIGHT_DEPARTURE_AIRPORT';
      case FilterTypes.ArrivalAirports:
        return 'FLIGHT_RESULTS_MODULE.FILTER_SECTION.FLIGHT_ARRIVAL_AIRPORT';
      case FilterTypes.Tags:
        return 'FLIGHT_RESULTS_MODULE.FILTER_SECTION.TAGS';
      case FilterTypes.AirFilterTabs:
        return 'HOTELS_MODULE.RESULT_HOTELS.SORTER_LABEL';
      case FilterTypes.DepartureTimes:
        return 'FLIGHT_RESULTS_MODULE.FILTER_SECTION.FLIGHT_DEPARTURE';
      case FilterTypes.PriceRange:
        return this.isFilterPrice ? 
          'FLIGHT_RESULTS_MODULE.FILTER_SECTION.PRICE_CURRENCY': 
          'FLIGHT_RESULTS_MODULE.FILTER_SECTION.PRICE';
      default:
        return FILTER_TITLES[label];
    }
  }

  public searchFlightsWithFilter(filter: FilterApplier): void {
    if (
      (!isNullOrUndefined(filter.removable) && !filter.removable) ||
      (isNullOrUndefined(filter.removable) && !isEmpty(filter.value))
    ) {
      this.trackSelectFilterAmplitudeEvent(filter);
      if (filter.type === FilterTypes.AirFilterTabs) {
        this.mobileFilterMagicContComponent?.close();
      }
    }
    if (this.isBuses) {
      this.busFacade.searchBusesWithFilter(filter, this.isMobile);
    } else if (this.isBySector && this.isMobile) {
      this.fligthfacade.searchFlightsWithSingleFilterBySector(filter);
    } else {
      this.fligthfacade.searchFlightsWithSingleFilter([filter], this.isMobile);
    }
    this.domManipulateService.resetScrollPosition();
  }

  public searchFlightsWithFilters(filters: FilterApplier[]): void {
    this.isBySector && this.isMobile ?
      this.fligthfacade.searchFlightsWithMultipleFiltersBySector(filters) :
      this.fligthfacade.searchFlightsWithMultipleFilters(filters, this.isMobile);
  }

  private trackSelectFilterAmplitudeEvent(filter: FilterApplier): void {
    iif(() => this.isBuses,
      this.amplitude.searchProperties$(SearchBy.BUSES).pipe(take(1)),
      this.amplitude.searchProperties$(SearchBy.FLIGHTS).pipe(take(1))
    ).pipe(
      take(1),
      switchMap(searchProps => iif(
        () => (filter.type === FilterTypes.DepartureTimes || filter.type === FilterTypes.SectorDurations),
        of({
          searchProps,
          eventProps: {
            FILTERGROUP: filter.type,
            FILTERVALUE: [...filter?.value as string[]].map(value => {
              const transformValue = filter.type === FilterTypes.DepartureTimes ? filterMappers.SECONDS_IN_HOURS : 1;
              return filterMappers.secondsToStringTime(parseFloat(value) * transformValue);
            })
          }
        }),
        of({ searchProps, eventProps: { FILTERGROUP: filter.type, FILTERVALUE: filter.value } })
      )),
      switchMap(({ eventProps, searchProps }) => this.triggerAmplitudeService.trackAmplitudeEventProps$(
        AMPLITUDE_EVENTS.SELECT_FILTER, eventProps, searchProps
      )),
      tap(trigger => triggerAmplitudeEventApp(trigger))
    ).subscribe().unsubscribe();
  }
}
